import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, RoleBadge, inputCls, labelCls, btnPrimary, roleMeta } from "@/components/kit";
import { UserCircle, Barbell, Target, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

const goals = [{ v: "cut", label: "Cut" }, { v: "maintain", label: "Maintain" }, { v: "bulk", label: "Bulk" }];

export default function Profile() {
  const { user, setUser } = useAuth();
  const isAthlete = user?.role === "athlete";
  const [form, setForm] = useState({ name: user?.name || "", sport: user?.sport || "", weight_kg: user?.weight_kg || "", height_cm: user?.height_cm || "", goal: user?.goal || "maintain" });
  const [macros, setMacros] = useState({ calories: user?.macro_goals?.calories || 2500, protein: user?.macro_goals?.protein || 180, carbs: user?.macro_goals?.carbs || 280, fat: user?.macro_goals?.fat || 70 });
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update password"); }
    finally { setChangingPw(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name };
      if (isAthlete) {
        Object.assign(payload, {
          sport: form.sport || null, weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
          height_cm: form.height_cm ? parseFloat(form.height_cm) : null, goal: form.goal,
          macro_goals: { calories: parseInt(macros.calories) || 0, protein: parseInt(macros.protein) || 0, carbs: parseInt(macros.carbs) || 0, fat: parseInt(macros.fat) || 0 },
        });
      }
      const r = await api.put("/profile", payload);
      setUser(r.data);
      toast.success("Profile updated");
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const rm = roleMeta[user?.role] || {};

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-3xl mx-auto">
      <PageHeader eyebrow="Account" title="Profile" icon={UserCircle} />

      <Card className="p-6 md:p-8 mb-6 flex items-center gap-4">
        <Avatar name={user?.name} size={64} color={rm.color} />
        <div>
          <div className="font-head text-2xl font-bold uppercase tracking-tight">{user?.name}</div>
          <div className="text-sm text-[#737373] mb-2">{user?.email}</div>
          <RoleBadge role={user?.role} />
        </div>
      </Card>

      <Card className="p-6 md:p-8 mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-6"><UserCircle size={16} weight="fill" color="#FF3B30" /> Details</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2"><label className={labelCls}>Name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" /></div>
          {isAthlete && <>
            <div className="md:col-span-2"><label className={labelCls}>Sport</label><input className={inputCls} value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} data-testid="profile-sport-input" /></div>
            <div><label className={labelCls}>Weight (kg)</label><input type="number" className={inputCls} value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="profile-weight-input" /></div>
            <div><label className={labelCls}>Height (cm)</label><input type="number" className={inputCls} value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} data-testid="profile-height-input" /></div>
          </>}
        </div>
        {isAthlete && (
          <div className="mt-6">
            <label className={labelCls}>Goal</label>
            <div className="grid grid-cols-3 gap-2">
              {goals.map((gg) => (
                <button key={gg.v} onClick={() => setForm({ ...form, goal: gg.v })} data-testid={`goal-${gg.v}`}
                  className={`p-3 rounded-sm font-head font-bold uppercase tracking-tight transition-colors ${form.goal === gg.v ? "bg-[#FF3B30] text-white" : "bg-[#0A0A0A] border border-white/15 text-[#A3A3A3]"}`}>{gg.label}</button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {isAthlete && (
        <Card className="p-6 md:p-8 mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-6"><Target size={16} weight="fill" color="#FF3B30" /> Daily Macro Goals</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div><label className={labelCls}>Calories</label><input type="number" className={inputCls} value={macros.calories} onChange={(e) => setMacros({ ...macros, calories: e.target.value })} data-testid="goal-calories-input" /></div>
            <div><label className={labelCls} style={{ color: "#FF3B30" }}>Protein (g)</label><input type="number" className={inputCls} value={macros.protein} onChange={(e) => setMacros({ ...macros, protein: e.target.value })} data-testid="goal-protein-input" /></div>
            <div><label className={labelCls} style={{ color: "#FF9F0A" }}>Carbs (g)</label><input type="number" className={inputCls} value={macros.carbs} onChange={(e) => setMacros({ ...macros, carbs: e.target.value })} data-testid="goal-carbs-input" /></div>
            <div><label className={labelCls} style={{ color: "#34C759" }}>Fat (g)</label><input type="number" className={inputCls} value={macros.fat} onChange={(e) => setMacros({ ...macros, fat: e.target.value })} data-testid="goal-fat-input" /></div>
          </div>
        </Card>
      )}

      <button onClick={save} disabled={saving} className={`${btnPrimary} w-full`} data-testid="save-profile-button">{saving ? "Saving…" : "Save changes"}</button>

      <Card className="p-6 md:p-8 mt-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-6"><LockKey size={16} weight="fill" color="#FF3B30" /> Change Password</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div><label className={labelCls}>Current password</label><input type="password" className={inputCls} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" /></div>
          <div><label className={labelCls}>New password</label><input type="password" className={inputCls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="At least 8 characters" data-testid="new-password-input" /></div>
          <div><label className={labelCls}>Confirm new</label><input type="password" className={inputCls} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="confirm-password-input" /></div>
        </div>
        <button onClick={changePassword} disabled={changingPw || !pw.current_password || !pw.new_password} className={`${btnPrimary} w-full mt-6`} data-testid="change-password-button">{changingPw ? "Updating…" : "Update password"}</button>
      </Card>
    </div>
  );
}
